import { Pill, RiCloseLine, Stack } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetAllPlotLabelValues } from '@/api/rest/resources/organization';
import { PlotLabelValue } from '@/api/rest/resources/types/plotGroup';

import { AddNewLabelDialog } from './AddNewLabelDialog';

type LabelPickerProps = {
  labelIds: string[];
  organizationId: string;
  onAdd: (label: PlotLabelValue) => void;
  onRemove: (labelId: string) => void;
};

type LabelPillProps = {
  label: PlotLabelValue;
  onRemove: (labelId: string) => void;
};

const LabelPill = ({ label, onRemove }: LabelPillProps) => {
  const { t } = useTranslation();

  return (
    <Pill key={label.id} size='small' className='bg-neutral-black-8 text-text-primary'>
      <div className='flex items-start gap-1'>
        <div className='flex flex-col leading-tight'>
          <span className='typography-caption text-text-secondary'>{label.label_name}</span>
          <span className='typography-body2'>{label.value}</span>
        </div>
        <button
          type='button'
          aria-label={t('shared.uploadPlots.labelPicker.removeAriaLabel', {
            name: `${label.label_name} ${label.value}`,
          })}
          onClick={() => onRemove(label.id)}
          className='mt-0.5 text-text-secondary hover:text-text-primary'
        >
          <RiCloseLine size={12} />
        </button>
      </div>
    </Pill>
  );
};

export const LabelPicker = ({ labelIds, organizationId, onAdd, onRemove }: LabelPickerProps) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [createdLabels, setCreatedLabels] = useState<PlotLabelValue[]>([]);
  const { data: existingLabelValuesData } = useGetAllPlotLabelValues({
    queryParams: { organization_id: organizationId },
  });
  const existingLabelValues = existingLabelValuesData.results;
  const selectedLabelIds = new Set(labelIds);
  const knownValueIds = new Set(existingLabelValues.map((value) => value.id));

  const handleCreated = (label: PlotLabelValue) => {
    setIsDialogOpen(false);
    setCreatedLabels((labels) => [...labels, label]);
    onAdd(label);
  };

  // Prefer the query-driven existingLabelValues for anything it already knows about, and
  // only fall back to the local echo for labels the query hasn't reflected yet — this avoids
  // rendering the same selected label twice.
  const selectedLabels = [
    ...existingLabelValues.filter((label) => selectedLabelIds.has(label.id)),
    ...createdLabels.filter((label) => selectedLabelIds.has(label.id) && !knownValueIds.has(label.id)),
  ];

  return (
    <>
      <Stack direction='row' className='flex-wrap gap-2'>
        {selectedLabels.map((label) => (
          <LabelPill key={label.id} label={label} onRemove={onRemove} />
        ))}
        <button
          type='button'
          onClick={() => setIsDialogOpen(true)}
          className='typography-body2Semibold rounded-full border border-dashed border-neutral-black-23 px-3 py-0.5 text-text-secondary hover:border-primary-100 hover:text-primary-100'
        >
          {t('shared.uploadPlots.labelPicker.addButton')}
        </button>
      </Stack>

      <AddNewLabelDialog
        open={isDialogOpen}
        organizationId={organizationId}
        existingLabelValues={existingLabelValues}
        onClose={() => setIsDialogOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
};
